import { useMemo, useState } from "react";
import { BlockLink } from "@/components/DrilldownLink";
import HeroStatusChip from "@/components/HeroStatusChip";
import IdentityActionSchoolsModal from "@/components/IdentityActionSchoolsModal";
import IdentityKpiSchoolsModal from "@/components/IdentityKpiSchoolsModal";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);
const pct = (v) => (typeof v === "number" ? `${v}%` : "N/A");

const formatLakhs = (total) => {
  if (!total) return "0L";
  return `${(total / 100000).toFixed(1)}L`;
};

const blockIdentityIndex = (block, districtApaarCoverage) => {
  const aadhaar = block.aadhaar_pct ?? 0;
  const apaar = block.apaar_pct ?? districtApaarCoverage;
  const nameMatch = 100 - (block.name_mismatch_pct ?? 0);
  return Math.round((aadhaar * 0.4 + apaar * 0.4 + nameMatch * 0.2) * 10) / 10;
};

const priorityTag = (index) => {
  if (index >= 90) return { label: "Good", className: "green" };
  if (index >= 80) return { label: "Medium", className: "" };
  return { label: "High", className: "red" };
};

const MetricDonut = ({ value, display, label, subtitle, variant = "" }) => (
  <div className="ed-id-metric-donut">
    <div className={`ed-id-donut ${variant}`} style={{ "--pct": `${Math.min(Math.max(value, 0), 100)}%` }}>
      <strong>{display ?? `${value}%`}</strong>
    </div>
    <b>{label}</b>
    <span>{subtitle}</span>
  </div>
);

const KpiCard = ({ label, value, hint, iconVariant, children, onClick, clickable = false }) => (
  <article
    className={`ed-id-kpi-card${clickable ? " clickable" : ""}`}
    onClick={clickable ? onClick : undefined}
    onKeyDown={
      clickable
        ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClick?.();
            }
          }
        : undefined
    }
    role={clickable ? "button" : undefined}
    tabIndex={clickable ? 0 : undefined}
    aria-label={clickable ? `${label}: ${value}. ${hint}. Click for details.` : undefined}
  >
    <div className="ed-id-kpi-top">
      <small>{label}</small>
      <span className={`ed-id-mini-icon ${iconVariant || ""}`}>{children}</span>
    </div>
    <strong>{value}</strong>
    <span>{hint}</span>
  </article>
);

const ExecutiveIdentityTab = ({ identityData, scopeLabel }) => {
  const [priorityOnly, setPriorityOnly] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [kpiModalMetric, setKpiModalMetric] = useState(null);

  const summary = identityData?.summary ?? {};
  const aadhaar = identityData?.aadhaar_metrics ?? {};
  const apaar = identityData?.apaar_metrics ?? {};
  const breakdown = identityData?.compliance_breakdown ?? [];
  const blocks = identityData?.block_performance ?? [];

  const identityIndex = summary.identity_compliance_index ?? 0;
  const aadhaarCoverage = aadhaar.aadhaar_coverage_pct ?? summary.aadhaar_coverage ?? 0;
  const apaarCoverage = apaar.apaar_coverage_pct ?? summary.apaar_coverage ?? 0;
  const nameMismatch = aadhaar.name_mismatch_rate ?? 0;
  const nameMatchRate = breakdown.find((b) => b.metric === "Name Match Rate")?.value ?? Math.round((100 - nameMismatch) * 10) / 10;
  const dataQuality = Math.max(
    0,
    Math.min(
      100,
      aadhaar.data_quality_pct ??
        breakdown.find((b) => b.metric === "Data Quality")?.value ??
        Math.round((100 - (aadhaar.exception_rate ?? 0)) * 10) / 10,
    ),
  );
  const apaarGap = Math.max(0, Math.round((100 - apaarCoverage) * 10) / 10);

  const followUpBlocks = useMemo(
    () => [...blocks].sort((a, b) => (a.aadhaar_pct ?? 0) - (b.aadhaar_pct ?? 0)).slice(0, 10),
    [blocks],
  );

  const worstMismatchBlocks = useMemo(
    () => [...blocks].sort((a, b) => (b.name_mismatch_pct ?? 0) - (a.name_mismatch_pct ?? 0)).slice(0, 3),
    [blocks],
  );

  const bestBlocks = useMemo(
    () => [...blocks].sort((a, b) => (b.aadhaar_pct ?? 0) - (a.aadhaar_pct ?? 0)).slice(0, 3),
    [blocks],
  );

  const actionBlockCount = useMemo(
    () => blocks.filter((b) => (b.aadhaar_pct ?? 0) < 93 || (b.name_mismatch_pct ?? 0) > 7).length,
    [blocks],
  );

  const tableRows = useMemo(() => {
    const rows = blocks.map((block) => {
      const idx = blockIdentityIndex(block, apaarCoverage);
      const blockApaar = block.apaar_pct ?? null;
      const tag = priorityTag(idx);
      return {
        ...block,
        name_match_pct: Math.round((100 - (block.name_mismatch_pct ?? 0)) * 10) / 10,
        apaar_pct: blockApaar,
        identity_index: idx,
        priority: tag,
      };
    });
    if (priorityOnly) {
      return rows.filter((r) => r.priority.className === "red");
    }
    return rows.sort((a, b) => (b.aadhaar_pct ?? 0) - (a.aadhaar_pct ?? 0));
  }, [blocks, apaarCoverage, priorityOnly]);

  const heroNarrative =
    aadhaarCoverage >= 90 && apaarCoverage < 90
      ? "Identity records are strong overall, but APAAR generation and name mismatch require targeted block-level follow-up to reach full compliance."
      : aadhaarCoverage >= 90
        ? "Identity records are strong across the district with sustained Aadhaar coverage and improving APAAR generation."
        : "Identity compliance needs block-level intervention to lift Aadhaar coverage and reduce mismatch risk.";

  const mismatchNames = worstMismatchBlocks.map((b) => b.block_name).filter(Boolean).join(", ") || "priority blocks";
  const benchmarkNames = bestBlocks.map((b) => b.block_name).filter(Boolean).join(", ") || "top blocks";

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="ed-content ed-identity-view" data-testid="executive-identity-tab">
      <section className="ed-hero-card ed-identity-hero">
        <div className="ed-identity-layout">
          <div className="ed-identity-summary">
            <div
              className="ed-identity-gauge"
              aria-label={`Identity index ${identityIndex}`}
              style={{
                background: `radial-gradient(circle at center, white 0 53%, transparent 54%), conic-gradient(var(--ed-primary) 0 ${identityIndex}%, rgba(37,99,235,.11) ${identityIndex}% 100%)`,
              }}
            >
              <div>
                <strong>{identityIndex}</strong>
                <span>Index</span>
              </div>
            </div>
            <div className="ed-health-copy">
              <h2>Student Overview</h2>
              <p>{heroNarrative}</p>
              <div className="ed-status-row">
                {aadhaarCoverage >= 90 && <HeroStatusChip chipClass="ed-status-chip" variant="green">Strong Aadhaar Base</HeroStatusChip>}
                <HeroStatusChip
                  chipClass="ed-status-chip"
                  variant="blue"
                  onClick={() => setKpiModalMetric("apaar_coverage")}
                  ariaLabel={`APAAR gap ${apaarGap} percent — view schools below target`}
                >
                  APAAR Gap: {apaarGap}%
                </HeroStatusChip>
                {nameMismatch > 5 && (
                  <HeroStatusChip
                    chipClass="ed-status-chip"
                    variant="amber"
                    onClick={() => setKpiModalMetric("name_mismatch")}
                    ariaLabel={`Name mismatch ${nameMismatch} percent — view schools with mismatches`}
                  >
                    Name Mismatch: {nameMismatch}%
                  </HeroStatusChip>
                )}
              </div>
            </div>
          </div>

          <div className="ed-id-kpi-grid" aria-label="Student identity KPI cards">
            <KpiCard label="Total Students" value={formatLakhs(summary.total_students)} hint="Enrolled students">
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-8 0v2" />
                <circle cx="12" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              </svg>
            </KpiCard>
            <KpiCard
              label="Aadhaar Coverage"
              value={pct(aadhaarCoverage)}
              hint="Healthy coverage"
              iconVariant="green"
              clickable
              onClick={() => setKpiModalMetric("aadhaar_coverage")}
            >
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </KpiCard>
            <KpiCard
              label="APAAR Coverage"
              value={pct(apaarCoverage)}
              hint="Needs acceleration"
              iconVariant="purple"
              clickable
              onClick={() => setKpiModalMetric("apaar_coverage")}
            >
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </KpiCard>
            <KpiCard
              label="Name Mismatch"
              value={pct(nameMismatch)}
              hint="Priority cleanup"
              iconVariant="orange"
              clickable
              onClick={() => setKpiModalMetric("name_mismatch")}
            >
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
              </svg>
            </KpiCard>
            <KpiCard label="Data Quality" value={pct(dataQuality)} hint="Verified records" iconVariant="cyan">
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 21h8" />
                <path d="M12 17v4" />
                <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
              </svg>
            </KpiCard>
            <KpiCard
              label="Action Blocks"
              value={actionBlockCount}
              hint="Require intervention"
              iconVariant="red"
              clickable={actionBlockCount > 0}
              onClick={() => actionBlockCount > 0 && setActionModalOpen(true)}
            >
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 3v18h18" />
                <path d="m19 9-5 5-4-4-3 3" />
              </svg>
            </KpiCard>
          </div>
        </div>
      </section>

      <div className="ed-section-title">
        <div>
          <h2>Compliance Diagnostics</h2>
          <p>Metrics are separated from the executive summary and shown as large, readable diagnostic cards instead of compressed visuals.</p>
        </div>
        <button type="button" className="ed-section-action" onClick={() => scrollTo("ed-id-actions")}>
          View action queue →
        </button>
      </div>

      <section className="ed-id-diagnostic-grid">
        <article className="ed-panel">
          <div className="ed-panel-head">
            <div>
              <h3>Compliance Metrics</h3>
              <p>Four core measures that define student identity readiness.</p>
            </div>
            <span className="ed-pill">Current month</span>
          </div>
          <div className="ed-id-gauge-grid">
            <MetricDonut value={aadhaarCoverage} label="Aadhaar Coverage" subtitle="Strong across most blocks" />
            <MetricDonut value={apaarCoverage} label="APAAR Generation" subtitle="Moderate gap remaining" variant="orange" />
            <MetricDonut value={nameMatchRate} label="Name Match Rate" subtitle={`${nameMismatch}% mismatch risk`} variant="red" />
            <MetricDonut value={identityIndex} display={String(identityIndex)} label="Identity Index" subtitle="Composite score" variant="blue" />
          </div>
        </article>

        <article className="ed-panel">
          <div className="ed-panel-head">
            <div>
              <h3>Block-Wise Aadhaar Coverage</h3>
              <p>Lowest-coverage blocks need targeted follow-up to lift district-wide identity readiness.</p>
            </div>
            <span className="ed-pill">Needs follow-up</span>
          </div>
          <div className="ed-bar-list" aria-label="Block-wise Aadhaar Coverage">
            {followUpBlocks.length ? (
              followUpBlocks.map((block) => (
                <div className="ed-hbar-row" key={block.block_code || block.block_name}>
                  <div className="ed-hbar-label">{block.block_name?.toUpperCase()}</div>
                  <div className="ed-hbar-track">
                    <div className="ed-hbar" style={{ width: `${block.aadhaar_pct ?? 0}%` }} />
                  </div>
                  <div className="ed-hbar-value">{pct(block.aadhaar_pct)}</div>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500">No block data available for the selected scope.</p>
            )}
          </div>
        </article>
      </section>

      <div className="ed-section-title" id="ed-id-actions">
        <div>
          <h2>Insights and Next Actions</h2>
          <p>Insights are converted into action cards so decision-makers can immediately see the issue, owner and recommended next step.</p>
        </div>
        <button type="button" className="ed-section-action" onClick={() => scrollTo("ed-id-table")}>
          View block table →
        </button>
      </div>

      <section className="ed-id-lower-grid">
        <article className="ed-id-insights-panel">
          <div className="ed-panel-head">
            <div>
              <h3>Identity Signals</h3>
              <p>Summarized from enrolment, Aadhaar, APAAR and name-matching records.</p>
            </div>
            <span className="ed-pill">Rule-based</span>
          </div>
          <div className="ed-id-insight-stack">
            <article className="ed-id-insight-card">
              <span className="ed-icon-box green">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m3 17 6-6 4 4 8-8" />
                  <path d="M14 7h7v7" />
                </svg>
              </span>
              <div>
                <h4>Coverage strength is high</h4>
                <p>
                  {fmt(summary.total_students)} students enrolled with Aadhaar coverage at {pct(aadhaarCoverage)}. Current identity index is {identityIndex}.
                </p>
              </div>
              <span className="ed-id-priority good">Strong</span>
            </article>
            <article className="ed-id-insight-card">
              <span className="ed-icon-box purple">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
              </span>
              <div>
                <h4>APAAR generation needs acceleration</h4>
                <p>APAAR coverage is {pct(apaarCoverage)}, creating a gap of {apaarGap}% against full compliance.</p>
              </div>
              <span className="ed-id-priority">Medium</span>
            </article>
            <article className="ed-id-insight-card">
              <span className="ed-icon-box orange">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                </svg>
              </span>
              <div>
                <h4>Name mismatch requires cleanup sprint</h4>
                <p>
                  Name mismatch rate is {pct(nameMismatch)}. Blocks such as {mismatchNames} should be prioritized first.
                </p>
              </div>
              <span className="ed-id-priority high">High</span>
            </article>
            <article className="ed-id-insight-card">
              <span className="ed-icon-box">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18" />
                  <path d="m19 9-5 5-4-4-3 3" />
                </svg>
              </span>
              <div>
                <h4>Top coverage blocks can become benchmarks</h4>
                <p>{benchmarkNames} can be used as reference blocks for operational process replication.</p>
              </div>
              <span className="ed-id-priority good">Benchmark</span>
            </article>
          </div>
        </article>

        <aside className="ed-id-action-panel">
          <h3>Recommended Action Queue</h3>
          <div className="ed-action-card">
            <article className="ed-action-item">
              <span className="ed-id-small-icon red">1</span>
              <div>
                <h4>Resolve name mismatch backlog</h4>
                <p>Start with {mismatchNames}. Assign block MIS officer and school data owners.</p>
              </div>
            </article>
            <article className="ed-action-item">
              <span className="ed-id-small-icon orange">2</span>
              <div>
                <h4>APAAR camp for pending students</h4>
                <p>Run weekly drives for blocks below district average until coverage crosses 90%.</p>
              </div>
            </article>
            <article className="ed-action-item">
              <span className="ed-id-small-icon green">3</span>
              <div>
                <h4>Replicate high-performing block workflow</h4>
                <p>Document {benchmarkNames} process patterns for district-wide adoption.</p>
              </div>
            </article>
          </div>
          <button type="button" className="ed-id-learn-more" onClick={() => scrollTo("ed-id-table")}>
            <span>Open action tracker</span>
            <span>→</span>
          </button>
        </aside>
      </section>

      <section className="ed-panel ed-table-card" id="ed-id-table">
        <div className="ed-table-head">
          <div>
            <h3>Block-Wise Identity Summary</h3>
            <p>Detailed block table for coverage review, mismatch cleanup and APAAR follow-up.</p>
          </div>
          <div className="ed-map-table-tools">
            <button
              type="button"
              className="ed-map-filter-chip"
              onClick={() => setPriorityOnly((v) => !v)}
            >
              {priorityOnly ? "Show all ▾" : "High priority only ▾"}
            </button>
          </div>
        </div>
        <table className="ed-table" aria-label="Block-wise identity summary">
          <thead>
            <tr>
              <th>Block</th>
              <th>Aadhaar %</th>
              <th>APAAR %</th>
              <th>Name Match %</th>
              <th>Mismatch %</th>
              <th>Identity Index</th>
              <th>Priority</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.length ? (
              tableRows.map((row) => (
                <tr key={row.block_code || row.block_name}>
                  <td>
                    {row.block_code ? (
                      <BlockLink blockCode={row.block_code}>{row.block_name}</BlockLink>
                    ) : (
                      row.block_name
                    )}
                  </td>
                  <td>{pct(row.aadhaar_pct)}</td>
                  <td>{row.apaar_pct != null ? pct(row.apaar_pct) : "—"}</td>
                  <td>{pct(row.name_match_pct)}</td>
                  <td>{pct(row.name_mismatch_pct)}</td>
                  <td className="ed-score">{row.identity_index}</td>
                  <td>
                    <span className={`ed-tag ${row.priority.className}`}>{row.priority.label}</span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7}>No block identity data available for {scopeLabel}.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <IdentityActionSchoolsModal
        open={actionModalOpen}
        onClose={() => setActionModalOpen(false)}
        scopeLabel={scopeLabel}
        actionBlockCount={actionBlockCount}
      />

      <IdentityKpiSchoolsModal
        open={Boolean(kpiModalMetric)}
        metric={kpiModalMetric}
        onClose={() => setKpiModalMetric(null)}
        scopeLabel={scopeLabel}
        districtPct={
          kpiModalMetric === "aadhaar_coverage"
            ? aadhaarCoverage
            : kpiModalMetric === "apaar_coverage"
              ? apaarCoverage
              : kpiModalMetric === "name_mismatch"
                ? nameMismatch
                : null
        }
      />
    </div>
  );
};

export default ExecutiveIdentityTab;
